import orgImg from "@/public/images/case-study/org.png";
import simpleImg from "@/public/images/services/custom.png";
import shirtbayImg from "@/public/images/services/custom.png";
import nujoomImg from "@/public/images/case-study/nujoom-zysoftec.png";
import sellerImg from "@/public/images/case-study/seller_app.png";
import cherryImg from "@/public/images/services/custom.png";

export const caseStudies = [
    {
        id: "orgupdate",
        name: "OrgUpdate",
        tagline: "Job Market Intelligence Platform Built for Reliable Data Collection",
        domain: "Data Intelligence",
        project_type: "SaaS Platform",
        users: "HR Analysts, Researchers & Data-Driven Organizations",
        delivery: "Full Product Build",
        duration: "4+ months",
        challenge: "The client needed reliable access to structured job listing data from Google Jobs. Existing third-party solutions repeatedly failed due to Google's frequent UI changes, resulting in unreliable data feeds, product delays, and an inability to launch confidently in a competitive HR tech market.",
        solution: "ZySoftec designed and developed a custom Puppeteer-based scraping engine that operated independently of Google's UI structure. The platform included adaptive scraping logic, data enrichment pipelines, advanced filtering capabilities, and real-time dashboards that transformed raw job listings into actionable market intelligence.",
        outcome: "The platform achieved 100% scraper uptime despite multiple Google UI changes, increased scraper stability by approximately 90%, and enabled a successful product launch that provided the client with a significant competitive advantage.",
        key_capabilities: [
        "Google Jobs data collection",
        "Adaptive web scraping",
        "Real-time analytics dashboards",
        "Advanced job filtering",
        "Exportable reporting",
        "Data enrichment pipelines"
        ],
        summary: "OrgUpdate is a SaaS platform built to provide real-time job market intelligence through large-scale data collection, enrichment, and visualization. Designed for HR analysts, researchers, and organizations that rely on labor market insights, the platform converts unstructured job listings into actionable business intelligence through dashboards, filters, and reporting tools.",
        what_needed: [
        "Reliable access to structured job listing data at scale",
        "A scraping system capable of surviving frequent Google UI changes",
        "Real-time dashboards for labor market analysis",
        "Advanced filtering and search capabilities",
        "Exportable reports for research and business use",
        "A scalable foundation for future product growth"
        ],
        what_built_layers: [
        {
        layer: "Data collection engine",
        detail: "Built a custom Puppeteer-based scraping engine using adaptive logic, smart DOM mapping, and robust error handling to ensure stability regardless of interface changes."
        },
        {
        layer: "Data enrichment pipeline",
        detail: "Developed processing workflows that cleaned, structured, categorized, and enriched raw job listing data into meaningful datasets."
        },
        {
        layer: "Analytics & visualization platform",
        detail: "Created real-time dashboards, advanced filtering tools, search functionality, and reporting modules designed specifically for HR and research workflows."
        }
        ],
        solution_delivered: [
        "UI-agnostic Google Jobs scraping engine",
        "Adaptive scraping and monitoring logic",
        "Data enrichment and transformation pipelines",
        "Real-time visualization dashboards",
        "Advanced job search and filtering",
        "CSV and PDF export functionality",
        "Dockerized deployment pipeline",
        "Vercel-hosted frontend infrastructure"
        ],
        outcomes: [
        "100% scraper uptime despite multiple Google UI changes",
        "~90% improvement in scraper stability",
        "Successful MVP and full product launch delivered on schedule",
        "Eliminated dependency on unreliable third-party tools",
        "Created a competitive advantage in the HR technology market",
        "Improved platform reliability and user confidence"
        ],
        tech_stack: [
        "Next.js",
        "Node.js",
        "MongoDB",
        "Puppeteer",
        "Chart.js",
        "Docker",
        "Railway",
        "Vercel"
        ],
        client_note: "We were stuck for months trying to fix a scraper issue no one else could solve. ZySoftec's team not only cracked the code but gave us the edge to finally launch our platform. Their technical knowledge, dedication, and problem-solving attitude were unmatched.",
        focus_tags: [
        "Web scraping",
        "Data intelligence",
        "SaaS platform",
        "Job market analytics",
        "Data visualization",
        "Reporting automation"
        ],
        image: orgImg,
        }
        ,
    {
        id: "simple-numbers",
        name: "Simple Numbers",
        tagline: "QuickBooks Analytics Platform with Multi-Company Data Access",
        domain: "Analytics",
        project_type: "Analytics Platform",
        users: "Accountants & Finance Teams",
        delivery: "Integration + Dashboard",
        duration: "3+ months",
        challenge: "Multi-company QuickBooks access without repeated logins and clean, dashboard-ready data.",
        solution: "Custom aggregation APIs with Redis-based session management and financial analytics modules.",
        outcome: "~80% reduction in login interruptions and 30-40% dashboard performance improvement.",
        key_capabilities: ["Multi-company data access", "Persistent sessions", "Financial analytics", "Period comparisons"],
        summary: "Simple Numbers is a cloud accounting analytics platform designed for accountants and financial services firms. ZySoftec built the integration and analytics layer needed to pull data across multiple QuickBooks companies in a unified workflow.",
        what_needed: [
            "Multi-company QuickBooks access without repeated logins",
            "Clean, dashboard-ready data instead of deeply nested structures",
            "Trend analysis and period comparisons (quarterly, yearly)",
            "Fast reporting workflows for finance professionals"
        ],
        what_built_layers: [
            {
                layer: "Aggregation middleware",
                detail: "Custom API to fetch and consolidate data across multiple QuickBooks accounts, then normalize it for analysis."
            },
            {
                layer: "Persistent authentication",
                detail: "Redis-based token management supporting sessions up to three months, reducing login friction."
            },
            {
                layer: "Financial analytics modules",
                detail: "Revenue breakdowns, period comparisons, benchmarks, and filtering by client segments."
            },
            {
                layer: "Dashboard experience",
                detail: "Charts and tables optimized for finance workflows with responsive performance."
            }
        ],
        solution_delivered: [
            "Custom aggregation APIs for multi-company data",
            "Redis-based token/session strategy",
            "Finance dashboards with comparisons and benchmarks",
            "Optimized data transformation and filtering"
        ],
        outcomes: [
            "~80% reduction in login interruptions",
            "Dashboard performance improved 30–40%",
            "Reporting workflows improved by ~60%",
            "Early customer adoption with long session continuity"
        ],
        tech_stack: ["MERN", "QuickBooks API", "Chart.js", "JWT", "Redis", "Docker", "Railway", "Vercel"],
        client_note: "QuickBooks support said multi-company access was not possible, but ZySoftec delivered it and made analysis feel effortless.",
        focus_tags: ["Integrations", "Session reliability", "Finance analytics"],
        image: simpleImg,
    },
    {
        id: "shirtbay",
        name: "Personalized (ShirtBay)",
        tagline: "Real-Time Custom Gifting Editor with Print-Ready Output",
        domain: "Commerce",
        project_type: "E-commerce Tool",
        users: "End Consumers",
        delivery: "Full Product Build",
        duration: "5+ months",
        challenge: "Smooth, real-time editing with print-ready output at 300 DPI across all devices.",
        solution: "Browser-based Fabric.js design engine with layered rendering and high-resolution export pipeline.",
        outcome: "100% browser-based customization with editing latency under 200ms and higher conversions.",
        key_capabilities: ["Real-time canvas editing", "Layered rendering", "Print-ready exports", "Cross-device support"],
        summary: "Personalized (ShirtBay) is a web platform that enables customers to design customized gifts in real time. ZySoftec built a Photoshop-like editing experience directly in the browser, with precise layered rendering and high-resolution print exports.",
        what_needed: [
            "Smooth, real-time editing for text, graphics, and layers",
            "Pixel-perfect alignment and responsive interactions",
            "Print-ready output at 300 DPI+",
            "Undo/redo, alignment helpers, templates, and live preview",
            "Works on desktop and mobile without plugins"
        ],
        what_built_layers: [
            {
                layer: "Browser-based design engine",
                detail: "Fabric.js + Canvas API integrated into React for real-time editing."
            },
            {
                layer: "Layered rendering system",
                detail: "Snapping, grouping, alignment guides, multi-layer control, and clean UX."
            },
            {
                layer: "High-resolution export pipeline",
                detail: "Converts on-screen designs into print-ready files at 300 DPI."
            },
            {
                layer: "Cross-device experience",
                detail: "Mobile-first optimization so editing stays responsive across devices."
            }
        ],
        solution_delivered: [
            "Browser-based design engine with Fabric.js",
            "Layered rendering with alignment tools",
            "300 DPI print-ready export pipeline",
            "Cross-device responsive editing"
        ],
        outcomes: [
            "100% browser-based customization with zero plugins",
            "Editing latency kept under 200 ms",
            "Higher conversions and fewer returns via accurate previews",
            "Users can start on one device and continue on another"
        ],
        tech_stack: ["MERN", "Fabric.js", "Canvas API", "TailwindCSS", "JWT", "Vercel", "Railway"],
        client_note: "The platform improved purchase confidence and reduced returns through a professional editing experience and reliable print output.",
        focus_tags: ["Real-time UI", "Canvas performance", "Print-ready exports"],
        image: simpleImg,
    },
    {
        id: "nujoom-ai",
        name: "Nujoom AI",
        tagline: "Voice-Interactive Document Q&A Platform",
        domain: "AI",
        project_type: "AI Product",
        users: "Knowledge Workers",
        delivery: "Full Product Build",
        duration: "4+ months",
        challenge: "Context-aware Q&A from uploaded documents with speech-to-speech interaction.",
        solution: "Document-to-vector pipeline with RAG-based AI and Azure Speech Services for voice interaction.",
        outcome: "90%+ speech recognition accuracy and ~60% improvement in document comprehension speed.",
        key_capabilities: ["Document processing", "RAG-based search", "Voice interaction", "Multilingual support"],
        summary: "Nujoom AI converts uploaded documents into voice-interactive AI chatbots. ZySoftec built the document processing pipeline, vector search system, and speech-to-speech experience required for low-latency, multilingual interactions.",
        what_needed: [
            "Upload and parse multiple file formats",
            "Context-aware question answering grounded in document content",
            "Speech-to-speech interaction with customizable voices",
            "Low latency conversational flow",
            "Self-serve product experience for non-technical users"
        ],
        what_built_layers: [
            {
                layer: "Document-to-knowledge pipeline",
                detail: "Files processed into vector embeddings to enable contextual retrieval."
            },
            {
                layer: "AI response layer",
                detail: "OpenAI + LangChain for semantic search, summarization, and grounded answers."
            },
            {
                layer: "Voice interaction layer",
                detail: "Azure Speech Services for speech-to-speech conversation with multilingual support."
            },
            {
                layer: "Product workflow UI",
                detail: "Upload, chatbot creation, voice settings, and dashboards to manage bots."
            }
        ],
        solution_delivered: [
            "Document-to-vector embedding pipeline",
            "RAG-based AI response system",
            "Speech-to-speech voice interaction",
            "Self-serve chatbot management UI"
        ],
        outcomes: [
            "90%+ speech recognition accuracy",
            "~60% improvement in document comprehension speed",
            "Voice-first interactions increased engagement"
        ],
        tech_stack: ["Next.js", "Node.js", "MongoDB", "OpenAI", "LangChain", "Azure Cognitive Services"],
        client_note: "Nujoom AI became a self-serve conversational assistant that speaks to files, delivered with clarity and strong product thinking.",
        focus_tags: ["RAG pipelines", "Voice UX", "Low-latency AI"],
        image: nujoomImg,
    },
    {
        id: "sellermetrics",
        name: "Seller Metrics",
        tagline: "Amazon Analytics Platform for Performance Monitoring and Growth",
        domain: "E-commerce",
        project_type: "SaaS Platform",
        users: "Amazon Sellers & Agencies",
        delivery: "Full Product Build",
        duration: "5+ months",
        challenge: "Amazon sellers lacked a centralized way to track profitability, advertising performance, inventory health, and business growth metrics across multiple accounts.",
        solution: "Built a comprehensive analytics platform integrating Amazon SP-API data with custom reporting, forecasting, and profitability dashboards.",
        outcome: "Reduced reporting time by over 80% and enabled sellers to make faster data-driven decisions across multiple stores.",
        key_capabilities: [
          "Amazon SP-API integrations",
          "Profitability dashboards",
          "Advertising analytics",
          "Inventory forecasting"
        ],
        summary: "Seller Metrics is a SaaS analytics platform built for Amazon sellers and agencies. The platform consolidates sales, advertising, inventory, and profitability data into one dashboard, providing actionable insights for growth and optimization.",
        what_needed: [
          "Unified visibility into Amazon business performance",
          "Automated reporting without spreadsheets",
          "Profitability calculations across products and campaigns",
          "Forecasting tools for inventory planning"
        ],
        what_built_layers: [
          {
            layer: "Data integration layer",
            detail: "Connected Amazon SP-API endpoints to collect orders, ads, inventory, and settlement data."
          },
          {
            layer: "Analytics engine",
            detail: "Built custom profitability calculations, forecasting models, and KPI aggregations."
          },
          {
            layer: "Dashboard experience",
            detail: "Interactive reports, visual analytics, and exportable performance summaries."
          }
        ],
        solution_delivered: [
          "Amazon SP-API integration",
          "Profitability reporting",
          "Inventory forecasting dashboards",
          "Advertising performance analytics"
        ],
        outcomes: [
          "80% reduction in manual reporting effort",
          "Faster decision-making through centralized analytics",
          "Improved inventory planning and profitability visibility"
        ],
        tech_stack: [
          "Next.js",
          "Node.js",
          "MongoDB",
          "Amazon SP-API",
          "Chart.js",
          "Docker",
          "AWS"
        ],
        client_note: "Seller Metrics provides Amazon sellers with a complete operational view of their business performance.",
        focus_tags: [
          "Amazon analytics",
          "Business intelligence",
          "E-commerce SaaS"
        ],
        image: sellerImg,
      },
    {
        id: "cherrysprites",
        name: "Cherrysprites",
        tagline: "Global Habit Tracking with Timezone-Smart Collaboration",
        domain: "Collaboration",
        project_type: "Consumer App",
        users: "Global Users",
        delivery: "Full Product Build",
        duration: "3+ months",
        challenge: "Habit schedules that work correctly across time zones with synchronized group challenges.",
        solution: "Timezone-aware scheduling engine with real-time sync and calendar-based tracking interface.",
        outcome: "Global users stayed aligned without timezone confusion with increased check-ins and engagement.",
        key_capabilities: ["Timezone-aware scheduling", "Real-time sync", "Social challenges", "Calendar tracking"],
        summary: "Cherrysprites is a social habit tracking app designed for global collaboration. ZySoftec built timezone-aware scheduling and real-time challenge synchronization so users across regions can stay aligned without confusion.",
        what_needed: [
            "Habit schedules that work correctly across time zones",
            "Group challenges with synchronized progress for global users",
            "Invite-based participation model",
            "Real-time updates and shared tracking",
            "Calendar-first UI for clarity and daily planning"
        ],
        what_built_layers: [
            {
                layer: "Timezone-aware habit logic",
                detail: "Schedules display locally while challenge logic stays consistent globally."
            },
            {
                layer: "Collaborative challenge system",
                detail: "Real-time sync engine for shared progress and accountability."
            },
            {
                layer: "Calendar-based tracking interface",
                detail: "Monthly calendar view for completions, streaks, and visibility."
            },
            {
                layer: "Social layer",
                detail: "Feeds and participation mechanics to improve consistency and engagement."
            }
        ],
        solution_delivered: [
            "Timezone-aware scheduling engine",
            "Real-time collaborative challenges",
            "Calendar-based habit tracking UI",
            "Social feeds and engagement features"
        ],
        outcomes: [
            "Global users stayed aligned without timezone confusion",
            "Calendar UI improved habit clarity and ease of use",
            "Social features increased check-ins and engagement",
            "Accurate timezone handling at scale"
        ],
        tech_stack: ["MongoDB", "Express.js", "React.js", "Node.js", "Moment-Timezone", "FullCalendar", "JWT", "Socket.io", "Vercel", "Railway"],
        client_note: "Timezone-based habit tracking became global and social through smart architecture and an intuitive user experience.",
        focus_tags: ["Timezone logic", "Real-time sync", "Calendar UX"],
        image: simpleImg,
    }
];